import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, DialogActions, DialogContent, Divider, IconButton, Stack, TextField, Typography } from '@mui/material';
import { AttachFile, Close, Inbox } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  useCreateCashOfficeVoucherMutation, useSendCashOfficeTransferMutation, useCreateCashOfficeTopupMutation,
  useCreateCashOfficeDepositMutation, useGetCashOfficeTransfersQuery, useReceiveCashOfficeTransferMutation,
} from '../../../services/cashOfficeApi';
import {
  P, fieldSx, money, num, isAmount, amountInput, useClientRef, Note, Label, AmountField, Field, AMOUNT_CODES, BILL_CODES, errMessage,
} from './cashOfficeShared';

const FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

// Dialog body (summary + action picker passed in as `top`) and footer, shared by every action.
function Shell({ top, children, submitLabel, onSubmit, disabled, loading, onClose }) {
  return (
    <>
      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {top}
        <Divider sx={{ borderColor: P.divider }} />
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pt: 1, pb: 2.5, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}
          sx={{ flex: 1, py: 1.1, fontWeight: 600, fontSize: '0.85rem', borderRadius: '8px', textTransform: 'none', borderColor: P.border, color: P.text2,
            '&:hover': { borderColor: P.disabled, bgcolor: P.surface } }}>
          {onSubmit ? 'Cancel' : 'Close'}
        </Button>
        {onSubmit && (
          <Button variant="contained" disableElevation onClick={onSubmit} disabled={disabled || loading}
            sx={{ flex: 2, py: 1.1, fontWeight: 700, fontSize: '0.85rem', borderRadius: '8px', textTransform: 'none', bgcolor: P.brand, color: '#fff',
              '&:hover': { bgcolor: P.brandHover }, '&.Mui-disabled': { bgcolor: P.border, color: P.muted } }}>
            {loading ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={15} sx={{ color: '#fff' }} /><span>Saving…</span></Stack> : submitLabel}
          </Button>
        )}
      </DialogActions>
    </>
  );
}

// ── Petty expense ────────────────────────────────────────────────────────────
export function ExpenseForm({ top, summary, readOnly, onClose, onDone }) {
  const boxes = useMemo(() => (summary?.cash_points || []).filter((p) => p.kind === 'petty_fund'), [summary]);
  const heads = useMemo(() => summary?.expense_heads || [], [summary]);
  const [pickedBox, setBoxId] = useState('');
  const [pickedHead, setHeadId] = useState('');
  const boxId = pickedBox || boxes[0]?.id || '';
  const headId = pickedHead || heads[0]?.id || '';
  const [moreHeads, setMoreHeads] = useState(false);
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [bill, setBill] = useState('');
  const [noBill, setNoBill] = useState('');
  const [photo, setPhoto] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [clientRef, renewRef] = useClientRef();
  const fileInput = useRef(null);
  const [create, { isLoading }] = useCreateCashOfficeVoucherMutation();

  const box = boxes.find((b) => b.id === Number(boxId));
  const head = heads.find((h) => h.id === Number(headId));
  const amt = num(amount);
  const short = box && isAmount(amount) && amt > Number(box.available);
  const billMissing = head?.requires_bill && !bill.trim() && !photo && !noBill.trim();
  const auto = box && head && isAmount(amount) && amt <= Number(box.auto_approve_limit) && !head.always_needs_approval;
  const clearErr = () => { setErrors({}); setFormError(''); };

  if (!summary?.permissions?.record_voucher) return <Shell top={top} onClose={onClose}><Note tone="wa">You can't record petty expenses at this store.</Note></Shell>;
  if (!boxes.length) return <Shell top={top} onClose={onClose}><Note tone="wa">This store has no petty cash box yet. Ask head office to set one up.</Note></Shell>;

  const pickPhoto = (file) => {
    if (!file) return;
    if (!FILE_TYPES.includes(file.type) || file.size > 5 * 1024 * 1024) {
      setErrors({ bill: 'Use a JPG, PNG or PDF up to 5 MB' });
      return;
    }
    setPhoto(file);
    clearErr();
  };

  const submit = async () => {
    const fields = { cash_point_id: box.id, expense_head_id: head.id, amount, payee_kind: 'other', payee_name: payee.trim(),
      bill_number: bill.trim() || undefined, no_bill_reason: bill.trim() ? undefined : noBill.trim() || undefined, client_ref: clientRef };
    let body = fields;
    if (photo) {
      body = new FormData();
      Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== '') body.append(k, v); });
      body.append('bills[]', photo);
    }
    try {
      const res = await create(body).unwrap();
      const v = res.data.voucher;
      const warnings = (res.data.warnings || []).map((w) => w.message);
      renewRef();
      onDone({
        number: v.number,
        text: v.status === 'approved' ? `${money(v.amount)} paid from ${v.cash_point.name} to ${v.payee.name}.` : `${money(v.amount)} saved for a manager to approve.`,
        warnings,
      });
    } catch (e) {
      const code = e?.data?.code;
      const msg = errMessage(e, 'Could not save the expense. Please try again.');
      if (AMOUNT_CODES.includes(code)) setErrors({ amount: msg });
      else if (BILL_CODES.includes(code)) setErrors({ bill: msg });
      else setFormError(msg);
    }
  };

  const shown = moreHeads ? heads : heads.slice(0, 6);
  return (
    <Shell top={top} onClose={onClose} onSubmit={submit} loading={isLoading} submitLabel="Save Expense"
      disabled={readOnly || !box || !head || !isAmount(amount) || !payee.trim() || short || billMissing}>
      {boxes.length > 1 && (
        <Field label="Petty cash box" required select value={boxId} onChange={(v) => { setBoxId(v); clearErr(); }}
          options={boxes.map((b) => ({ value: b.id, label: `${b.name} · ${money(b.available)} available` }))} />
      )}
      <Box>
        <Label>Expense head</Label>
        <Box sx={{ display: 'flex', flexWrap: { xs: 'nowrap', sm: 'wrap' }, overflowX: { xs: 'auto', sm: 'visible' }, gap: 0.75, pb: 0.25,
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          {shown.map((h) => (
            <Chip key={h.id} label={h.name} clickable onClick={() => { setHeadId(h.id); clearErr(); }} aria-pressed={Number(headId) === h.id}
              sx={{ height: 30, borderRadius: '16px', fontSize: '0.74rem', fontWeight: 600, flexShrink: 0, border: '1px solid',
                ...(Number(headId) === h.id ? { bgcolor: P.brandBg, borderColor: P.brand, color: P.brand } : { bgcolor: '#fff', borderColor: P.border, color: P.secondary }) }} />
          ))}
          {heads.length > 6 && (
            <Chip label={moreHeads ? 'Fewer' : `More (${heads.length - 6})`} clickable onClick={() => setMoreHeads((m) => !m)} variant="outlined"
              sx={{ height: 30, borderRadius: '16px', fontSize: '0.74rem', fontWeight: 600, flexShrink: 0, borderColor: P.border, color: P.text2 }} />
          )}
        </Box>
      </Box>
      <AmountField value={amount} onChange={(v) => { setAmount(v); clearErr(); }} error={errors.amount}
        disabled={readOnly} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5 }}>
        <Field label="Paid to" required value={payee} onChange={(v) => { setPayee(v); clearErr(); }} placeholder="Shop or person" disabled={readOnly} />
        <Field label="Bill number" value={bill} onChange={(v) => { setBill(v); clearErr(); }} error={errors.bill} disabled={readOnly} />
      </Box>
      <Box>
        <input ref={fileInput} type="file" hidden accept="image/jpeg,image/png,application/pdf" capture="environment"
          onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ''; }} />
        {photo ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ border: `1px solid ${P.border}`, borderRadius: '8px', px: 1.25, py: 0.75 }}>
            <AttachFile sx={{ fontSize: 16, color: P.text2 }} />
            <Typography noWrap sx={{ flex: 1, fontSize: '0.8rem', color: P.text }}>{photo.name}</Typography>
            <IconButton size="small" aria-label="Remove bill photo" onClick={() => setPhoto(null)}><Close sx={{ fontSize: 16 }} /></IconButton>
          </Stack>
        ) : (
          <Button fullWidth variant="outlined" startIcon={<AttachFile />} onClick={() => fileInput.current?.click()} disabled={readOnly}
            sx={{ height: 40, borderStyle: 'dashed', borderRadius: '8px', textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderColor: P.disabled, color: P.text2 }}>
            Take bill photo
          </Button>
        )}
      </Box>
      {!bill.trim() && !photo && (
        <Field label={head?.requires_bill ? 'No bill? Say why' : 'No bill? Why (optional)'} required={!!head?.requires_bill} value={noBill}
          onChange={(v) => { setNoBill(v); clearErr(); }} placeholder="e.g. Roadside stall" disabled={readOnly}
          helper={head?.requires_bill ? 'Add the bill number or photo, or say why there is none' : undefined} />
      )}
      {short && <Note tone="er">{box.name} has {money(box.available)} available. Top it up first.</Note>}
      {!short && isAmount(amount) && box && head && (
        <Note tone={auto ? 'ok' : 'wa'}>
          {auto ? `Within ${money(box.auto_approve_limit)}: paid from the box now.` : 'Saved for a manager; the box pays when approved.'}
        </Note>
      )}
      {formError && <Note tone="er">{formError}</Note>}
    </Shell>
  );
}

// ── Drawer → safe / petty box / bank ─────────────────────────────────────────
export function DrawerForm({ kind, top, summary, session, drawerExpected, readOnly, onClose, onDone }) {
  const points = summary?.cash_points || [];
  const banks = summary?.bank_accounts || [];
  const perms = summary?.permissions || {};
  const targets = points.filter((p) => p.kind === (kind === 'drop' ? 'safe' : 'petty_fund'));
  const [pickedTarget, setTargetId] = useState('');
  const [pickedBank, setBankId] = useState('');
  const [slip, setSlip] = useState('');
  const [typedAmount, setAmount] = useState(null); // null: not typed yet (a top-up starts at the suggested amount)
  const [note, setNote] = useState('');
  const [amountError, setAmountError] = useState('');
  const [formError, setFormError] = useState('');
  const [clientRef, renewRef] = useClientRef();
  const [send, { isLoading: sending }] = useSendCashOfficeTransferMutation();
  const [topup, { isLoading: topping }] = useCreateCashOfficeTopupMutation();
  const [deposit, { isLoading: depositing }] = useCreateCashOfficeDepositMutation();

  const targetId = pickedTarget || targets[0]?.id || '';
  const bankId = pickedBank || summary?.default_bank_account_id || banks[0]?.id || '';
  const target = targets.find((t) => t.id === Number(targetId));
  const need = target && kind === 'topup' ? Math.max(Number(target.float_amount || 0) - Number(target.balance), 0) : null;
  const amount = typedAmount ?? (need > 0 ? String(need) : '');
  const max = kind === 'topup' ? Math.min(drawerExpected ?? Infinity, Number(target?.float_amount || 0)) : drawerExpected;
  const amt = num(amount);
  const over = isAmount(amount) && max !== null && max !== undefined && amt > max;
  const differs = kind === 'topup' && need !== null && isAmount(amount) && amt !== need;
  const allowed = kind === 'deposit' ? perms.deposit : kind === 'topup' ? perms.topup : perms.send;
  const noSession = !session || session.status !== 'open';

  const blocker = !allowed ? `You can't ${kind === 'deposit' ? 'record bank deposits' : kind === 'topup' ? 'top up petty cash' : 'send cash to the safe'} at this store.`
    : noSession ? 'Open a cash session first: this comes out of the drawer.'
      : !targets.length && kind !== 'deposit' ? `This store has no ${kind === 'drop' ? 'safe' : 'petty cash box'} yet.`
        : kind === 'deposit' && !banks.length ? 'No bank account is set up yet. Ask head office.' : null;
  if (blocker) return <Shell top={top} onClose={onClose}><Note tone="wa">{blocker}</Note></Shell>;

  const submit = async () => {
    setFormError('');
    try {
      let number;
      let text;
      if (kind === 'deposit') {
        const res = await deposit({ bank_account_id: bankId, amount, method: 'branch_deposit', slip_number: slip.trim(), client_ref: clientRef }).unwrap();
        number = res.data.deposit.number;
        text = `${money(amount)} on its way to ${res.data.deposit.bank_account.name}.`;
      } else if (kind === 'topup') {
        const res = await topup({ cash_point_id: target.id, amount, reason: note.trim() || undefined, client_ref: clientRef }).unwrap();
        number = res.data.topup.number;
        text = res.data.topup.status === 'pending_approval'
          ? `${money(amount)} for ${target.name} is waiting for approval.`
          : `${money(amount)} sent to ${target.name}; the box keeper counts it in.`;
      } else {
        const res = await send({ to_cash_point_id: target.id, amount, purpose: 'safe_drop', notes: note.trim() || undefined, client_ref: clientRef }).unwrap();
        number = res.data.transfer.number;
        text = `${money(amount)} sent to ${target.name}; waiting to be counted in.`;
      }
      renewRef();
      onDone({ number, text, drawerChanged: true });
    } catch (e) {
      const msg = errMessage(e, 'Could not record it. Please try again.');
      if (AMOUNT_CODES.includes(e?.data?.code)) setAmountError(msg);
      else setFormError(msg);
    }
  };

  const label = { drop: 'Send to Safe', topup: 'Send to Petty Cash', deposit: 'Record Deposit' }[kind];
  return (
    <Shell top={top} onClose={onClose} onSubmit={submit} submitLabel={label} loading={sending || topping || depositing}
      disabled={readOnly || !isAmount(amount) || over || (kind === 'deposit' && (!bankId || !slip.trim())) || (kind !== 'deposit' && !target) || (differs && !note.trim())}>
      {kind === 'topup' && target && <Note tone="in">{target.name} needs {money(need)} to reach its {money(target.float_amount)} float.</Note>}
      {kind !== 'deposit' && targets.length > 1 && (
        <Field label="To" required select value={targetId} onChange={setTargetId} options={targets.map((t) => ({ value: t.id, label: t.name }))} />
      )}
      {kind === 'drop' && targets.length === 1 && <Typography sx={{ fontSize: '0.8rem', color: P.text2 }}>To <Box component="b" sx={{ color: P.text }}>{target?.name}</Box></Typography>}
      {kind === 'deposit' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5 }}>
          <Field label="Bank" required select value={bankId} onChange={setBankId}
            options={banks.map((b) => ({ value: b.id, label: `${b.name}${b.id === summary?.default_bank_account_id ? ' (default)' : ''}` }))} />
          <Field label="Deposit slip #" required value={slip} onChange={setSlip} />
        </Box>
      )}
      <AmountField value={amount} onChange={(v) => { setAmount(v); setAmountError(''); }} disabled={readOnly}
        error={amountError || (over ? (kind === 'topup' && amt > Number(target?.float_amount || 0) ? `At most the float, ${money(target.float_amount)}` : `More than the drawer should hold (${money(drawerExpected)})`) : '')} />
      {kind !== 'deposit' && (
        <Field label={differs ? 'Why a different amount' : 'Note'} required={differs} value={note} onChange={setNote} />
      )}
      {formError && <Note tone="er">{formError}</Note>}
      <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>
        {kind === 'deposit' ? 'Recorded as a Cash Out on this session; head office confirms the bank credit.'
          : `Recorded as a Cash Out on this session; in transit until the ${kind === 'drop' ? 'manager' : 'box keeper'} counts it in.`}
      </Typography>
    </Shell>
  );
}

// ── Receive cash sent to this store ──────────────────────────────────────────
function IncomingCard({ t, meId, canReceive, session, readOnly, onReceived }) {
  const [counting, setCounting] = useState(false);
  const [counted, setCounted] = useState('');
  const [error, setError] = useState('');
  const [receive, { isLoading }] = useReceiveCashOfficeTransferMutation();
  const toDrawer = t.to.kind === 'drawer';
  const mine = t.sent_by?.id === meId;
  const diff = counted === '' ? 0 : num(counted) - Number(t.amount);

  const submit = async () => {
    setError('');
    try {
      await receive({ id: t.id, counted_amount: counted }).unwrap();
      onReceived({ number: t.number, text: diff ? `Received ${money(counted)}; ${money(diff)} sent to the manager's differences.` : `Received ${money(counted)}.`, drawerChanged: toDrawer });
    } catch (e) {
      setError(errMessage(e, 'Could not receive it. Please try again.'));
    }
  };

  let action;
  if (mine) action = <Typography sx={{ fontSize: '0.68rem', color: P.muted, textAlign: 'right' }}>You sent it; someone else counts it in</Typography>;
  else if (!canReceive) action = <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>Not allowed here</Typography>;
  else if (toDrawer && (!session || session.status !== 'open')) action = <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>Open a session to count it in</Typography>;
  else if (!counting) {
    action = (
      <Button size="small" variant="contained" disableElevation disabled={readOnly} onClick={() => { setCounting(true); setCounted(String(Number(t.amount))); }}
        sx={{ height: 28, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none', borderRadius: '6px', bgcolor: P.brand, '&:hover': { bgcolor: P.brandHover } }}>
        Count In
      </Button>
    );
  }

  return (
    <Box data-testid="incoming-transfer" sx={{ border: `1px solid ${P.border}`, borderRadius: '10px', p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: P.text }}>{t.number}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: P.text2 }}>{t.from.label} → {t.to.label}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: P.text }}>{money(t.amount)}</Typography>
          {action}
        </Box>
      </Stack>
      {counting && (
        <Box sx={{ mt: 1.25 }}>
          <TextField fullWidth size="small" label="Counted amount" required autoFocus value={counted}
            onChange={(e) => { const v = amountInput(e.target.value); if (v !== null) { setCounted(v); setError(''); } }}
            inputProps={{ inputMode: 'decimal' }} sx={fieldSx} />
          {diff !== 0 && counted !== '' && (
            <Box sx={{ mt: 1 }}><Note tone={diff < 0 ? 'er' : 'wa'}>{diff < 0 ? 'Short' : 'Over'} by {money(Math.abs(diff))}. It goes to the manager's differences.</Note></Box>
          )}
          {error && <Box sx={{ mt: 1 }}><Note tone="er">{error}</Note></Box>}
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Button fullWidth variant="outlined" onClick={() => setCounting(false)} disabled={isLoading}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: P.border, color: P.text2, fontWeight: 600 }}>Cancel</Button>
            <Button fullWidth variant="contained" disableElevation onClick={submit} disabled={isLoading || counted === '' || readOnly}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: P.success, fontWeight: 700, '&:hover': { bgcolor: '#15803D' } }}>
              {isLoading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : 'Receive Cash'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export function ReceiveForm({ top, summary, session, storeId, readOnly, onClose, onDone }) {
  const meId = useSelector((s) => s.auth?.user?.id);
  const { data, isLoading, isError, refetch } = useGetCashOfficeTransfersQuery();
  const incoming = useMemo(() => (data?.data?.transfers || []).filter((t) => t.status === 'sent' && t.to.store_id === storeId), [data, storeId]);
  let body;
  if (isLoading) body = <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: P.brand }} /></Box>;
  else if (isError) {
    body = (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography sx={{ fontSize: '0.82rem', color: P.muted, mb: 1 }}>Couldn't load the cash on its way.</Typography>
        <Button onClick={refetch} sx={{ textTransform: 'none', color: P.brand, fontWeight: 700 }}>Retry</Button>
      </Box>
    );
  } else if (!incoming.length) {
    body = (
      <Box sx={{ textAlign: 'center', py: 4, color: P.muted }}>
        <Inbox sx={{ fontSize: 40, opacity: 0.5 }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 1 }}>Nothing on its way to this store.</Typography>
      </Box>
    );
  } else {
    body = (
      <Stack spacing={1}>
        {incoming.map((t) => (
          <IncomingCard key={t.id} t={t} meId={meId} canReceive={!!summary?.permissions?.receive} session={session} readOnly={readOnly} onReceived={onDone} />
        ))}
      </Stack>
    );
  }
  return <Shell top={top} onClose={onClose}>{body}</Shell>;
}
